import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';

import useScrollPosition from '../../hooks/useScrollPosition';
import CustomButton from './CustomButton';
import withRoutingProps from '../../hooks/withRoutingProps';

/* PROPERTIES:
-------------------------------------------
Ex: const menuOption = {
      buttonDropdown: {
        id: string (optional)
        style: <style>, (optional)
        className: <class-name>, (optional),
        rightClick: (boolean | default - false)
      },
      dropdownToggle: {
        toggleIcon, (bool)
        tag, (default button)
        style: <style>, (optional)
        disabled: (boolean),
        className: '' (optional)
        size: (optional | default - 'sm')
        color: <class-name>, (optional)
        outline: boolean, (optional: For design point of view, (true) showing below arrow icon in button)
        label: <text>,
        icon: (boolean),
        children: Add JSX content (optional)
      },
      dropdownMenu: {
        className: '',
        width: '<>px',
        options: [
          {
            id: <id> (optional)
            className: ''
            name: <name> (optional)
            dataAction: <action-name> (optional)
            dataIndex: "" (optional)
            dataValue: "" (optional)
            label: 'Dealership',
            disabled: boolean (optional)
            icon: 'fa-user-plus text-primary', (class name)
            onClick: e => handleClick(e) (event name pass),
            isvisible: boolean (optional),
            children: Add JSX content (optional)
            tag: define your tag,
            notDropdownClass: boolean (optional),
          },
        ]
      }
    };
*/

const ContextMenuComponent = ({ menuOption, isLightTheme }) => {
  const contextMenuRef = useRef(null);
  const scrollPosition = useScrollPosition();

  const [isContextMenuVisible, setContextMenuVisible] = useState(false);
  const [mouseLocation, setMouseLocation] = useState(null);
  const [contextMenuStyle, setContextMenuStyle] = useState(null);
  const [menuVisibilityOpacity, setMenuVisibilityOpacity] = useState(0);
  const [menuElementRef, setMenuElementRef] = useState(contextMenuRef?.current);

  const { buttonDropdown, dropdownToggle, dropdownMenu } = menuOption ?? {};

  const {
    id: btnDropdownId = '',
    style: btnDropdownStyle = {},
    className: btnDropdownClassName = '',
    rightClick = false
  } = buttonDropdown ?? {};
  const {
    toggleIcon,
    tag: Tag = CustomButton,
    style: dropdownToggleStyle = {},
    disabled: dropdownToggleDisabled = false,
    className: dropdownToggleClassName = '',
    color: dropdownToggleColor = isLightTheme ? 'secondary' : 'dark-300',
    size: dropdownToggleSize = 'sm',
    outline: dropdownToggleOutline = false,
    icon: dropdownToggleIcon = false,
    label: dropdownToggleLabel = null,
    children: dropdownToggleChildren = null,
    ignoreButtonClass: dropdownToggleIgnoreButtonClass = false
  } = dropdownToggle ?? {};
  const {
    options: dropdownOptions = {},
    className: dropdownMenuClassName = '',
    width: dropdownMenuWidth = ''
  } = dropdownMenu ?? {};

  const allInvisible = useMemo(
    () => dropdownOptions?.every(item => item?.isvisible === false),
    [dropdownOptions]
  );

  const isControlVisible = useMemo(
    () => dropdownOptions?.some(item => item?.isvisible === undefined || item?.isvisible === true),
    [dropdownOptions]
  );

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        isContextMenuVisible &&
        contextMenuRef?.current &&
        !contextMenuRef?.current?.contains(event.target)
      ) {
        setContextMenuVisible(false);
        setMenuVisibilityOpacity(0);
      }
    };
    const handleScroll = () => {
      if (isContextMenuVisible) {
        setContextMenuVisible(false);
        setMenuVisibilityOpacity(0);
      }
    };
    const handleResize = () => {
      if (isContextMenuVisible) {
        const { clientX, clientY } = mouseLocation;
        setMouseLocation({ clientX, clientY });
      }
    };

    setMenuElementRef(contextMenuRef?.current);

    const tableContainers = document.querySelectorAll('.table-container');
    tableContainers?.forEach(container => {
      container.addEventListener('scroll', handleScroll);
    });

    document.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      tableContainers?.forEach(container => {
        container.removeEventListener('scroll', handleScroll);
      });

      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [contextMenuRef, isContextMenuVisible]);

  const getMenuPosition = (x = 0, y = 0) => {
    const newMenuStyles = { top: y, left: x };
    if (!contextMenuRef?.current) {
      return newMenuStyles;
    }

    const { innerWidth, innerHeight } = window;
    const rect = contextMenuRef?.current?.getBoundingClientRect();

    newMenuStyles.top += scrollPosition;

    if (y + rect.height > innerHeight) {
      newMenuStyles.top -= rect.height;
    }
    if (x + rect.width > innerWidth) {
      newMenuStyles.left -= rect.width;
    }
    if (newMenuStyles.top < 0) {
      newMenuStyles.top = rect.height < innerHeight ? (innerHeight - rect.height) / 2 : 0;
    }
    if (newMenuStyles.left < 0) {
      newMenuStyles.left = rect.width < innerWidth ? (innerWidth - rect.width) / 2 : 0;
    }
    return newMenuStyles;
  };

  useEffect(() => {
    if (menuElementRef) {
      const { clientX, clientY } = mouseLocation;
      const newMenuStyles = getMenuPosition(clientX, clientY);
      setContextMenuStyle(newMenuStyles);
      setTimeout(() => {
        setMenuVisibilityOpacity(1);
      }, 10);
    }
  }, [mouseLocation, menuElementRef]);

  const handleContextMenu = event => {
    event.preventDefault();
    const { clientX, clientY } = event;
    setMouseLocation({ clientX, clientY });
    if (!isContextMenuVisible) {
      setContextMenuVisible(true);
    }
  };

  return isControlVisible ? (
    <>
      <div id={btnDropdownId} style={btnDropdownStyle} className={`${btnDropdownClassName}`}>
        {Tag == 'div' ? (
          <Tag
            className={`${
              toggleIcon !== undefined && toggleIcon !== null && !toggleIcon ? '' : 'dropdown-toggle'
            } ${dropdownToggleClassName}`}
            style={dropdownToggleStyle}
            disabled={dropdownToggleDisabled}
            onClick={!rightClick ? handleContextMenu : null} // Left-click event
            onContextMenu={rightClick ? handleContextMenu : null} // Right-click event
            color={dropdownToggleColor}
            size={dropdownToggleSize}
          >
            {dropdownToggleIcon && <i className="fa fa-gear" />}
            {dropdownToggleLabel}
            {dropdownToggleChildren}
          </Tag>
        ) : (
          <Tag
            className={`${
              toggleIcon !== undefined && toggleIcon !== null && !toggleIcon ? '' : 'dropdown-toggle'
            } ${dropdownToggleClassName}`}
            style={dropdownToggleStyle}
            disabled={dropdownToggleDisabled}
            onClick={!rightClick ? handleContextMenu : null} // Left-click event
            onContextMenu={rightClick ? handleContextMenu : null} // Right-click event
            color={dropdownToggleColor}
            outline={dropdownToggleOutline}
            size={dropdownToggleSize}
            ignoreButtonClass={dropdownToggleIgnoreButtonClass}
          >
            {dropdownToggleIcon && <i className="fa fa-gear" />}
            {dropdownToggleLabel}
            {dropdownToggleChildren}
          </Tag>
        )}
      </div>

      {!allInvisible && isContextMenuVisible && (
        <React.Fragment>
          {ReactDOM.createPortal(
            <div
              ref={contextMenuRef}
              className={`dropdown-menu ${isLightTheme ? 'bg-dark-400' : 'bg-dark-900'} ${
                isContextMenuVisible ? 'show' : ''
              } ${dropdownMenuClassName}`}
              style={{
                ...contextMenuStyle,
                zIndex: 99999,
                opacity: menuVisibilityOpacity,
                transition: 'opacity 0.1s cubic-bezier(0.18, 0.67, 0.6, 1.22)', // ease-in-out
                overflowWrap: 'break-word',
                width: dropdownMenuWidth
              }}
            >
              {dropdownOptions?.map((item, index) => {
                const itemindex = index;
                const status = item?.isvisible || item?.isvisible === undefined;
                if (!status) {
                  return null;
                }
                return (
                  <div
                    key={`dropdownOptions-${item?.label ?? ''}-${itemindex}`}
                    className={`${item.notDropdownClass ? '' : 'dropdown-item'} ${item?.className || ''} ${
                      item.disabled ? 'disabled' : ''
                    }`}
                    data-action={item?.dataAction}
                    data-id={item?.id}
                    data-name={item?.name}
                    data-index={item?.dataIndex || itemindex}
                    data-value={item?.dataValue}
                    onClick={e => {
                      if (item?.preventDefaultClick) {
                        e.setContextMenuVisible = setContextMenuVisible;
                        e.setMenuVisibilityOpacity = setMenuVisibilityOpacity;
                      }
                      if (item?.onClick) {
                        item?.onClick(e);
                      }
                      if (item?.preventDefaultClick) {
                        return;
                      }
                      setContextMenuVisible(false);
                      setMenuVisibilityOpacity(0);
                    }}
                    disabled={item?.disabled}
                    // extraa ADD
                    data-vin={item?.dataVin}
                    data-make={item?.dataMake}
                    data-model={item?.dataModel}
                    data-inv_id={item?.dataInvId}
                    data-veh={item?.dataVeh}
                    data-type={item?.dataType}
                    data-sertype={item?.dataSertype}
                  >
                    {item?.icon && <i className={`fa fa-fw ${item?.icon} mr-2`} />}
                    {item?.label}
                    {item?.children}
                  </div>
                );
              })}
            </div>,
            document.getElementById('root')
          )}
        </React.Fragment>
      )}
    </>
  ) : null;
};

ContextMenuComponent.displayName = 'ContextMenuComponent';
export default withRoutingProps(ContextMenuComponent);
